// web/src/screens/admin/text_effect_editor.ts — Plan 11-07 Task 1.
//
// Grapheme-aware text-effect editor (D-11 + Post-Research D-11 CORRECTION).
//
// CRITICAL: count graphemes manually. The segmenter's numeric .index field
// is a code-unit offset — NOT a grapheme ordinal. Same lock as the renderer
// at web/src/components/text_effect.ts (lines 6-11, 47-50).
//
// Latvian-specific note (Post-Research D-11 CORRECTION): single-codepoint
// diacritics (š ē ž ļ ķ ū ī č) ARE single graphemes. Latvian digraphs
// (`dž`, `dz`) are NOT clustered by UAX #29 — they are TWO graphemes each.
// Empirically verified 2026-05-09:
//
//   Intl.Segmenter("lv", {granularity:"grapheme"}).segment("džungļi")
//   produces 7 segments at ordinals 0..6 (d, ž, u, n, g, ļ, i).
//
// To highlight `dž` author MUST check both the d (ord N) and ž (ord N+1)
// boxes — the editor MUST NOT special-case digraphs. The renderer makes
// the same assumption; symmetric correctness is the entire point of T-11-14.
//
// T-11-09b lock: every grapheme rendered via element.textContent — never
// raw-HTML setters. Same admin-screen XSS gate as Plan 11-06.
//
// API contract (D-11 / D-12):
//   - effect_type: "plain" | "highlighted_letter" | "missing_letter"
//   - highlighted_letter → checkboxes; letter_indexes is the SET of selected ordinals.
//   - missing_letter     → radios;     letter_indexes is exactly one ordinal.
//   - "plain" should not call this editor at all (no controls to render).
//
// The displayed `word` argument is the linked Text's lv Translation content
// per D-12 — the CALLER is responsible for resolving short_code → translation.
// This module does NOT fetch; it just renders + emits onChange.

import type { AdminTextEffectDTO } from "../../types/api.js";

// v3.2 (migration 0038) the editor accepts an inline text-effect DTO. The
// `text_short_code` field is kept on the call-site shim for backward
// compatibility with existing callers but is no longer required.
export type TextEffectInput = AdminTextEffectDTO & { text_short_code?: string };

/**
 * mountTextEffectEditor renders one control per grapheme of `word`, wired
 * into `letter_indexes` per the rules above. Returns an unmount fn that
 * clears `root`.
 *
 * Caller: pass the FULL initial AdminTextWithEffectDTO (effect_type +
 * text_short_code + language_code + letter_indexes); the editor preserves
 * unrelated fields when emitting onChange so the caller can `Object.assign`
 * the result back into the Task's text_with_effect slot.
 */
export function mountTextEffectEditor(
  root: HTMLElement,
  word: string,
  initial: TextEffectInput,
  onChange: (next: TextEffectInput) => void,
): () => void {
  root.replaceChildren();
  const segmenter = new Intl.Segmenter("lv", { granularity: "grapheme" });
  const indexes = new Set<number>(initial.letter_indexes ?? []);
  const isMissing = initial.effect_type === "missing_letter";

  const wrap = document.createElement("div");
  wrap.setAttribute(
    "style",
    "display: flex; flex-wrap: wrap; gap: 4px; padding: 8px; background: white; border: 1px solid #e5e7eb; border-radius: 4px;",
  );

  // CRITICAL: manual ordinal counter. NEVER read segmenter's numeric .index —
  // that is a UTF-16 code-unit offset, not a grapheme ordinal.
  let ordinal = 0;
  for (const { segment } of segmenter.segment(word)) {
    const ord = ordinal;

    const label = document.createElement("label");
    label.setAttribute(
      "style",
      "display: inline-flex; flex-direction: column; align-items: center; gap: 4px; padding: 6px 8px; min-width: 36px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; cursor: pointer; font-family: system-ui, sans-serif;",
    );

    const segSpan = document.createElement("span");
    segSpan.textContent = segment; // textContent ONLY (T-11-09b)
    segSpan.setAttribute(
      "style",
      "display: block; font-size: 1.5em; font-weight: 700; color: #111827; line-height: 1;",
    );
    label.appendChild(segSpan);

    const ordSpan = document.createElement("span");
    ordSpan.textContent = String(ord); // textContent on the ordinal too
    ordSpan.setAttribute("style", "font-size: 11px; color: #6b7280;");
    label.appendChild(ordSpan);

    const ctl = document.createElement("input");
    ctl.type = isMissing ? "radio" : "checkbox";
    ctl.name = "letter-effect";
    ctl.value = String(ord);
    ctl.checked = indexes.has(ord);
    ctl.dataset.grapheme = segment;
    ctl.dataset.ordinal = String(ord);
    ctl.addEventListener("change", () => {
      if (isMissing) {
        // Single-selection (radio): replace with [ord] when checked, [] when not.
        if (ctl.checked) {
          indexes.clear();
          indexes.add(ord);
        } else {
          indexes.delete(ord);
        }
        onChange({ ...initial, letter_indexes: ctl.checked ? [ord] : [] });
      } else {
        // Multi-selection (checkbox): toggle ord in the set, emit sorted list.
        if (ctl.checked) indexes.add(ord);
        else indexes.delete(ord);
        onChange({
          ...initial,
          letter_indexes: [...indexes].sort((a, b) => a - b),
        });
      }
    });
    label.appendChild(ctl);

    wrap.appendChild(label);
    ordinal++;
  }

  root.appendChild(wrap);

  return () => {
    root.replaceChildren();
  };
}
